import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiFileText, FiCheck, FiAlertCircle, FiCreditCard, FiUser } from 'react-icons/fi';

export default function TermsOfService() {
  const lastUpdated = "January 31, 2026";

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
            <FiArrowLeft className="w-4 h-4" />
            <span>Back to FirstCut</span>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
              <span className="text-white font-bold text-sm">F</span>
            </div>
            <span className="text-white font-semibold">FirstCut</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center">
              <FiFileText className="w-6 h-6 text-violet-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Terms of Service</h1>
              <p className="text-slate-400">Last updated: {lastUpdated}</p>
            </div>
          </div>
        </div>

        <div className="prose prose-invert prose-slate max-w-none">
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 mb-8">
            <p className="text-slate-300 leading-relaxed">
              Welcome to FirstCut. By accessing or using our AI-powered content generation platform, you agree to be bound by these Terms of Service. Please read them carefully.
            </p>
          </div>

          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <FiCheck className="w-5 h-5 text-violet-400" />
              <h2 className="text-xl font-semibold text-white m-0">1. Acceptance of Terms</h2>
            </div>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5">
              <p className="text-slate-300">
                By creating an account or using FirstCut, you agree to these Terms of Service and our Privacy Policy. If you do not agree, please do not use our service. These terms apply to all users of the platform.
              </p>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-white mb-4">2. Description of Service</h2>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5">
              <p className="text-slate-300 mb-4">FirstCut is an AI-powered content generation platform that provides:</p>
              <ul className="text-slate-300 space-y-2 list-disc pl-5">
                <li>AI-generated content ideas for social media and marketing</li>
                <li>AI-generated images using advanced image generation models</li>
                <li>Client and campaign management tools</li>
                <li>Content novelty checking to ensure freshness</li>
                <li>Learning system that adapts to your preferences</li>
              </ul>
            </div>
          </section>

          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <FiUser className="w-5 h-5 text-violet-400" />
              <h2 className="text-xl font-semibold text-white m-0">3. User Accounts</h2>
            </div>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5 space-y-4">
              <div>
                <h3 className="text-white font-medium mb-2">Account Creation</h3>
                <p className="text-slate-300">
                  You must create an account using Google OAuth to use FirstCut. You are responsible for maintaining the confidentiality of your account and for all activities under your account.
                </p>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">Account Requirements</h3>
                <ul className="text-slate-300 space-y-2 list-disc pl-5">
                  <li>You must be at least 18 years old</li>
                  <li>You must provide accurate account information</li>
                  <li>You may not share your account with others</li>
                  <li>You must notify us of any unauthorized access</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <FiCreditCard className="w-5 h-5 text-violet-400" />
              <h2 className="text-xl font-semibold text-white m-0">4. Payment and Credits</h2>
            </div>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5 space-y-4">
              <div>
                <h3 className="text-white font-medium mb-2">Credit-Based System</h3>
                <p className="text-slate-300">
                  FirstCut uses a credit-based system. You can purchase credits through subscription plans or one-time credit packs. Credits are consumed when generating content or images.
                </p>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">Payment Terms</h3>
                <ul className="text-slate-300 space-y-2 list-disc pl-5">
                  <li>Payments are processed securely via Razorpay</li>
                  <li>All purchases are one-time payments (no automatic recurring charges)</li>
                  <li>Prices are in Indian Rupees (INR)</li>
                  <li>All fees are non-refundable except as specified in our Refund Policy</li>
                </ul>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">Credit Types</h3>
                <ul className="text-slate-300 space-y-2 list-disc pl-5">
                  <li><strong>Subscription Credits:</strong> Included with plan purchases, valid for your billing period</li>
                  <li><strong>Bonus Credits:</strong> Purchased via credit packs, never expire</li>
                  <li>System uses subscription credits first, then bonus credits</li>
                  <li>Credits cannot be transferred or exchanged for cash</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-white mb-4">5. Acceptable Use</h2>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5">
              <p className="text-slate-300 mb-4">You agree NOT to use FirstCut to:</p>
              <ul className="text-slate-300 space-y-2 list-disc pl-5">
                <li>Generate illegal, harmful, or offensive content</li>
                <li>Infringe on intellectual property rights of others</li>
                <li>Generate content involving minors inappropriately</li>
                <li>Spread misinformation or deceptive content</li>
                <li>Violate any applicable laws or regulations</li>
                <li>Attempt to bypass content moderation systems</li>
                <li>Reverse engineer or copy our proprietary algorithms</li>
              </ul>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-white mb-4">6. AI Content & Digital Credentials</h2>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5 space-y-4">
              <div>
                <h3 className="text-white font-medium mb-2">Content Credentials (C2PA)</h3>
                <p className="text-slate-300">
                  All AI-generated images from FirstCut include Content Credentials (C2PA metadata) - an industry standard adopted by Adobe, Google, Microsoft, and other major technology companies. This metadata identifies content as AI-generated or AI-enhanced.
                </p>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">Social Media Labeling</h3>
                <p className="text-slate-300">
                  Social media platforms (Instagram, Facebook, etc.) may automatically detect and label AI-generated content with small disclosure badges such as "AI info" or "Made with AI". This is standard practice across the industry and applies to all AI-generated content, not just FirstCut.
                </p>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">Your Responsibilities</h3>
                <ul className="text-slate-300 space-y-2 list-disc pl-5">
                  <li>You are responsible for compliance with local content disclosure laws when posting AI-generated content</li>
                  <li>You understand that FirstCut content may be labeled as AI-generated on social platforms</li>
                  <li>You will not attempt to remove or tamper with Content Credentials metadata</li>
                  <li>You will not use AI-generated content to mislead or deceive audiences about the nature of products or services</li>
                </ul>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">Content Types</h3>
                <ul className="text-slate-300 space-y-2 list-disc pl-5">
                  <li><strong>AI-Enhanced:</strong> Your uploaded photos with AI-improved lighting, mood, or styling</li>
                  <li><strong>AI-Styled:</strong> Your product photos placed in AI-generated scenes or backgrounds</li>
                  <li><strong>AI-Generated:</strong> Fully AI-created visuals with no user-uploaded source material</li>
                </ul>
                <p className="text-slate-400 text-sm mt-2">
                  Different content types may receive different levels of AI disclosure on social platforms.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-white mb-4">7. Intellectual Property</h2>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5 space-y-4">
              <div>
                <h3 className="text-white font-medium mb-2">Your Content</h3>
                <p className="text-slate-300">
                  You retain ownership of content you create using FirstCut. By using our service, you grant us a license to process and store your content as needed to provide the service.
                </p>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">Our Platform</h3>
                <p className="text-slate-300">
                  FirstCut, including our proprietary AI algorithms (novelty checking, learning systems, validation), branding, and platform code, remains our exclusive property.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <FiAlertCircle className="w-5 h-5 text-violet-400" />
              <h2 className="text-xl font-semibold text-white m-0">8. Disclaimers and Limitations</h2>
            </div>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5 space-y-4">
              <div>
                <h3 className="text-white font-medium mb-2">AI-Generated Content</h3>
                <p className="text-slate-300">
                  Content generated by AI may not always be accurate, appropriate, or suitable for your needs. You are responsible for reviewing and editing all generated content before use.
                </p>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">Service Availability</h3>
                <p className="text-slate-300">
                  We strive for high availability but cannot guarantee uninterrupted service. We are not liable for any downtime or service interruptions.
                </p>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">Limitation of Liability</h3>
                <p className="text-slate-300">
                  To the maximum extent permitted by law, FirstCut shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the service.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-white mb-4">9. Termination</h2>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5">
              <p className="text-slate-300 mb-4">
                We may suspend or terminate your account if you violate these terms. You may cancel your subscription at any time through your account settings.
              </p>
              <p className="text-slate-300">
                Upon termination, your right to use the service ceases immediately. Data retention after termination is governed by our Privacy Policy.
              </p>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-white mb-4">10. Changes to Terms</h2>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5">
              <p className="text-slate-300">
                We may modify these terms at any time. We will notify you of material changes via email or through the platform. Continued use after changes constitutes acceptance of the new terms.
              </p>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-white mb-4">11. Governing Law</h2>
            <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-5">
              <p className="text-slate-300">
                These terms are governed by the laws of India. Any disputes shall be resolved in the courts of Hyderabad, Telangana, India.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">11. Contact</h2>
            <div className="bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 border border-violet-500/30 rounded-lg p-5">
              <p className="text-slate-300 mb-3">
                For questions about these Terms of Service, please contact us:
              </p>
              <ul className="text-slate-300 space-y-2">
                <li><strong>Email:</strong> <a href="mailto:legal@firstcut.me" className="text-violet-400 hover:text-violet-300">legal@firstcut.me</a></li>
                <li><strong>Website:</strong> <a href="https://firstcut.me" className="text-violet-400 hover:text-violet-300">https://firstcut.me</a></li>
              </ul>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center text-slate-500 text-sm">
          <p>© 2026 FirstCut. All rights reserved.</p>
          <div className="flex justify-center gap-6 mt-4">
            <Link to="/terms" className="hover:text-slate-300 transition-colors">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-slate-300 transition-colors">Privacy Policy</Link>
            <Link to="/refund" className="hover:text-slate-300 transition-colors">Refund Policy</Link>
            <Link to="/contact-us" className="hover:text-slate-300 transition-colors">Contact Us</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
